package ai.wagemix.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initMethodologyPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("methodology.md", FrontMatterElement.Builder { addScalar("title", "WageMix Methodology"); }))
}

@Page("/methodology")
@Layout("ai.wagemix.components.layouts.MarkdownLayout")
@Composable
fun MethodologyPage() {
    org.jetbrains.compose.web.dom.H1(attrs = { id("methodology") }) {
        org.jetbrains.compose.web.dom.Text("Methodology")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Application:")
        }
        org.jetbrains.compose.web.dom.Text(" WageMix")
        org.jetbrains.compose.web.dom.Br()
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Purpose:")
        }
        org.jetbrains.compose.web.dom.Text(" A data analysis tool for HR professionals using BLS OEWS data to analyze salary percentiles and trends for individual or mixed occupations.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("1-overview") }) {
        org.jetbrains.compose.web.dom.Text("1. Overview")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("objective") }) {
        org.jetbrains.compose.web.dom.Text("Objective")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This document describes the methodology used by ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("WageMix")
        }
        org.jetbrains.compose.web.dom.Text(" to process, interpret, and visualize U.S. Bureau of Labor Statistics (BLS) data for HR and compensation analysis.")
        org.jetbrains.compose.web.dom.Br()
        org.jetbrains.compose.web.dom.Text("The application enables HR professionals to explore percentile-based salary trends over the past five years, identify pay benchmarks, and model composite roles that span multiple occupations.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("scope") }) {
        org.jetbrains.compose.web.dom.Text("Scope")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Data Source: ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("U.S. Bureau of Labor Statistics – Occupational Employment and Wage Statistics (OEWS)")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Time Coverage: Last five years of published OEWS data")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Metrics: Percentile-based wage estimates (P10, P25, Median, P75, P90)")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Capabilities:")
            org.jetbrains.compose.web.dom.Ul {
                org.jetbrains.compose.web.dom.Li {
                    org.jetbrains.compose.web.dom.Text("View wage trends by occupation")
                }
                org.jetbrains.compose.web.dom.Li {
                    org.jetbrains.compose.web.dom.Text("Combine multiple occupations into a blended profile")
                }
                org.jetbrains.compose.web.dom.Li {
                    org.jetbrains.compose.web.dom.Text("Use AI to infer relevant occupations from job descriptions")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("intended-audience") }) {
        org.jetbrains.compose.web.dom.Text("Intended Audience")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("HR analysts, compensation specialists, and business leaders involved in workforce planning and pay benchmarking.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("2-data-source") }) {
        org.jetbrains.compose.web.dom.Text("2. Data Source")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("dataset") }) {
        org.jetbrains.compose.web.dom.Text("Dataset")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The application uses the ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Occupational Employment and Wage Statistics (OEWS)")
        }
        org.jetbrains.compose.web.dom.Text(" dataset provided by the ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("U.S. Bureau of Labor Statistics (BLS)")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("URL:")
            }
            org.jetbrains.compose.web.dom.Text(" ")
            com.varabyte.kobweb.silk.components.navigation.Link("https://www.bls.gov/oes/") {
                org.jetbrains.compose.web.dom.Text("https://www.bls.gov/oes/")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Coverage:")
            }
            org.jetbrains.compose.web.dom.Ul {
                org.jetbrains.compose.web.dom.Li {
                    org.jetbrains.compose.web.dom.Text("Over 800 Standard Occupational Classification (SOC) codes")
                }
                org.jetbrains.compose.web.dom.Li {
                    org.jetbrains.compose.web.dom.Text("Data available at national, state, and metropolitan statistical area levels")
                }
                org.jetbrains.compose.web.dom.Li {
                    org.jetbrains.compose.web.dom.Text("Annual updates, typically reflecting May data")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("data-fields-used") }) {
        org.jetbrains.compose.web.dom.Text("Data Fields Used")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Occupation code (SOC)")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Occupation title")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Employment estimate")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Hourly and annual wage estimates at P10, P25, Median (P50), P75, and P90")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Year of publication")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("3-data-ingestion-and-processing") }) {
        org.jetbrains.compose.web.dom.Text("3. Data Ingestion and Processing")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("acquisition") }) {
        org.jetbrains.compose.web.dom.Text("Acquisition")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Automated retrieval from official BLS sources (CSV or XLSX format).")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Version control maintained for each year’s release.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Metadata (retrieval date, dataset version, and checksum) stored for reproducibility.")
        }
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("cleaning-and-normalization") }) {
        org.jetbrains.compose.web.dom.Text("Cleaning and Normalization")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("SOC codes standardized to the latest schema.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Wage data normalized into consistent ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("annualized")
            }
            org.jetbrains.compose.web.dom.Text(" format.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Missing or suppressed fields marked with internal quality flags (see Section 7).")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Outliers reviewed using percentile validation.")
        }
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("storage-and-indexing") }) {
        org.jetbrains.compose.web.dom.Text("Storage and Indexing")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Data stored in a structured relational or analytical database.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Indexed by ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("SOC code")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("year")
            }
            org.jetbrains.compose.web.dom.Text(", and ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("geographic level")
            }
            org.jetbrains.compose.web.dom.Text(" for efficient retrieval.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Separate tables maintain metadata and suppression indicators.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("4-data-interpretation") }) {
        org.jetbrains.compose.web.dom.Text("4. Data Interpretation")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("percentile-definitions") }) {
        org.jetbrains.compose.web.dom.Text("Percentile Definitions")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("P10 (10th percentile):")
            }
            org.jetbrains.compose.web.dom.Text(" 10% of workers earn less than this amount.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("P25 (25th percentile):")
            }
            org.jetbrains.compose.web.dom.Text(" 25% earn less; represents the lower quartile.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Median (P50):")
            }
            org.jetbrains.compose.web.dom.Text(" The midpoint of the wage distribution.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("P75 and P90:")
            }
            org.jetbrains.compose.web.dom.Text(" Represent higher quartiles and top earners in the occupation.")
        }
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("trend-analysis") }) {
        org.jetbrains.compose.web.dom.Text("Trend Analysis")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Trends computed as ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("year-over-year percent changes")
            }
            org.jetbrains.compose.web.dom.Text(" for each percentile.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Optionally adjusted for inflation using CPI-U if configured.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Visualization highlights movement in wage distribution over five years.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("5-occupation-mixing-logic") }) {
        org.jetbrains.compose.web.dom.Text("5. Occupation Mixing Logic")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("purpose") }) {
        org.jetbrains.compose.web.dom.Text("Purpose")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("To allow users to analyze ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("hybrid or cross-functional roles")
        }
        org.jetbrains.compose.web.dom.Text(" that are not represented by a single SOC occupation.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("method") }) {
        org.jetbrains.compose.web.dom.Text("Method")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Each occupation in a mix is assigned a ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("weight")
        }
        org.jetbrains.compose.web.dom.Text(" (percentage).")
        org.jetbrains.compose.web.dom.Br()
        org.jetbrains.compose.web.dom.Text("The tool computes a ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("weighted average")
        }
        org.jetbrains.compose.web.dom.Text(" for each percentile as follows:")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("[ BlendedPercentile(p) = sum_{i=1}^{n} w_i times P_{i,p} ]")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Where:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("( w_i ) = weight of occupation ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("i")
            }
            org.jetbrains.compose.web.dom.Text(" (summing to 1.0)")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("( P_{i,p} ) = percentile ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("p")
            }
            org.jetbrains.compose.web.dom.Text(" (e.g., P10) of occupation ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("i")
            }
        }
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("example") }) {
        org.jetbrains.compose.web.dom.Text("Example")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("If a role is modeled as ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("70% Software Developer")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("30% Data Scientist")
        }
        org.jetbrains.compose.web.dom.Text(":")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("[ Median_{blend} = 0.7 \\times Median_{Dev} + 0.3 \\times Median_{DS} ]")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This calculation is performed for each percentile (P10–P90).")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("validation") }) {
        org.jetbrains.compose.web.dom.Text("Validation")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Ensures weighted percentiles preserve correct ordering (P10 < P25 < Median < P75 < P90).")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Prevents outlier influence by capping extreme percentile deviations.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("6-ai-based-job-description-parsing") }) {
        org.jetbrains.compose.web.dom.Text("6. AI-Based Job Description Parsing")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("goal") }) {
        org.jetbrains.compose.web.dom.Text("Goal")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("To automatically infer relevant occupations from a job description text.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("process") }) {
        org.jetbrains.compose.web.dom.Text("Process")
    }
    org.jetbrains.compose.web.dom.Ol {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Text Extraction:")
            }
            org.jetbrains.compose.web.dom.Text(" Job description text cleaned and tokenized.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Model Inference:")
            }
            org.jetbrains.compose.web.dom.Text(" NLP model (e.g., transformer-based classifier) predicts relevant SOC codes.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Scoring:")
            }
            org.jetbrains.compose.web.dom.Text(" Model outputs confidence scores for each occupation.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Weight Conversion:")
            }
            org.jetbrains.compose.web.dom.Text(" Confidence scores normalized into mix weights.")
        }
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("user-control") }) {
        org.jetbrains.compose.web.dom.Text("User Control")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The user can modify occupation selections and adjust mix sliders.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Saved mixes are stored with metadata (user ID, creation date, AI source).")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Edited mixes can be reused or compared to new job descriptions.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("7-handling-data-anomalies-and-suppression") }) {
        org.jetbrains.compose.web.dom.Text("7. Handling Data Anomalies and Suppression")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("bls-suppression-and-bounds") }) {
        org.jetbrains.compose.web.dom.Text("BLS Suppression and Bounds")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The BLS may ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("suppress")
        }
        org.jetbrains.compose.web.dom.Text(" or ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("cap")
        }
        org.jetbrains.compose.web.dom.Text(" wage data to maintain confidentiality or account for small sample sizes.")
        org.jetbrains.compose.web.dom.Br()
        org.jetbrains.compose.web.dom.Text("Typical examples:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Replacing exact values with thresholds (e.g., “>= ${'$'}100.00/hour”)")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Omitting percentile data entirely for underrepresented occupations")
        }
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("application-treatment") }) {
        org.jetbrains.compose.web.dom.Text("Application Treatment")
    }
    org.jetbrains.compose.web.dom.H4(attrs = { id("1-upper-bound-values") }) {
        org.jetbrains.compose.web.dom.Text("1. Upper-Bound Values")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Represented in the UI as “≥ ${'$'}X” when published explicitly by BLS.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("When necessary, ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("inferred")
            }
            org.jetbrains.compose.web.dom.Text(" using a bounded regression from available percentiles (e.g., estimate P90 using median and P75 slopes).")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("All inferred points are tagged with an ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("estimated")
            }
            org.jetbrains.compose.web.dom.Text(" quality flag.")
        }
    }
    org.jetbrains.compose.web.dom.H4(attrs = { id("2-suppressed-or-missing-data") }) {
        org.jetbrains.compose.web.dom.Text("2. Suppressed or Missing Data")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Labeled as “Insufficient Data” in tables and charts.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Excluded from occupation mixes by default.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("If a mix includes suppressed values, the affected percentile is computed only from available data.")
        }
    }
    org.jetbrains.compose.web.dom.H4(attrs = { id("3-quality-indicators") }) {
        org.jetbrains.compose.web.dom.Text("3. Quality Indicators")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Each data point carries a quality flag:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Flag")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Description")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("D")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Direct BLS published value")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("B")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Bounded (e.g., ≥ ${'$'}X)")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("E")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Estimated via interpolation or regression")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("S")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Suppressed / Not available")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("transparency") }) {
        org.jetbrains.compose.web.dom.Text("Transparency")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("All visualizations include tooltips or footnotes describing estimated or bounded values.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("8-data-validation-and-quality-control") }) {
        org.jetbrains.compose.web.dom.Text("8. Data Validation and Quality Control")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("cross-checks") }) {
        org.jetbrains.compose.web.dom.Text("Cross-Checks")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Validate national aggregates against official BLS totals.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Confirm percentile ordering (P10 < P25 < Median < P75 < P90).")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Compare year-over-year trends to detect anomalies.")
        }
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("change-detection") }) {
        org.jetbrains.compose.web.dom.Text("Change Detection")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Identify occupations with >50% percentile shifts.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Mark as ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("outlier candidates")
            }
            org.jetbrains.compose.web.dom.Text(" for manual review.")
        }
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("auditability") }) {
        org.jetbrains.compose.web.dom.Text("Auditability")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Maintain full provenance for every record (source year, retrieval date, processing timestamp).")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Versioned data pipeline ensures historical reproducibility.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("9-output-and-visualization") }) {
        org.jetbrains.compose.web.dom.Text("9. Output and Visualization")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("charts") }) {
        org.jetbrains.compose.web.dom.Text("Charts")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Percentile band charts showing P10–P90 across five years.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Line and area visualizations for median trend and distribution width.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Overlay charts for blended occupations.")
        }
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("interactivity") }) {
        org.jetbrains.compose.web.dom.Text("Interactivity")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Occupation selection and weight sliders.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Toggle visibility for individual percentiles.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("10-limitations-and-caveats") }) {
        org.jetbrains.compose.web.dom.Text("10. Limitations and Caveats")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Data Lag:")
            }
            org.jetbrains.compose.web.dom.Text(" OEWS data represents prior-year estimates, not real-time wages.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Geographic Aggregation:")
            }
            org.jetbrains.compose.web.dom.Text(" National averages may obscure regional differences.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Model Bias:")
            }
            org.jetbrains.compose.web.dom.Text(" AI mappings depend on training coverage; niche roles may be underrepresented.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Suppression Uncertainty:")
            }
            org.jetbrains.compose.web.dom.Text(" Estimates for upper-bounded data introduce potential variance.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Weighting Assumptions:")
            }
            org.jetbrains.compose.web.dom.Text(" Occupation mixes assume linear wage blending, which may oversimplify complex role structures.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("11-future-enhancements") }) {
        org.jetbrains.compose.web.dom.Text("11. Future Enhancements")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Incorporate ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("inflation-adjusted")
            }
            org.jetbrains.compose.web.dom.Text(" or ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("cost-of-living")
            }
            org.jetbrains.compose.web.dom.Text(" normalization.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Add ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("regional filtering")
            }
            org.jetbrains.compose.web.dom.Text(" for metro/state-level insights.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Introduce ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("confidence intervals")
            }
            org.jetbrains.compose.web.dom.Text(" for blended percentiles.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Expand AI model integration with ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("O*NET skill and task taxonomy")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Enable ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("time-adjusted forecasting")
            }
            org.jetbrains.compose.web.dom.Text(" using regression or ARIMA models.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("12-references") }) {
        org.jetbrains.compose.web.dom.Text("12. References")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("U.S. Bureau of Labor Statistics. ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("Occupational Employment and Wage Statistics (OEWS)")
            }
            org.jetbrains.compose.web.dom.Text(".")
            org.jetbrains.compose.web.dom.Br()
            com.varabyte.kobweb.silk.components.navigation.Link("https://www.bls.gov/oes/") {
                org.jetbrains.compose.web.dom.Text("https://www.bls.gov/oes/")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("U.S. Bureau of Labor Statistics. ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("Standard Occupational Classification (SOC) System")
            }
            org.jetbrains.compose.web.dom.Text(".")
            org.jetbrains.compose.web.dom.Br()
            com.varabyte.kobweb.silk.components.navigation.Link("https://www.bls.gov/soc/") {
                org.jetbrains.compose.web.dom.Text("https://www.bls.gov/soc/")
            }
        }
    }
}
